import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function CrewSettings() {
  const { t } = useApp();
  const [mine, setMine] = useState([]);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const load = () => api.get("/crews/mine").then(r => setMine(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const h = setTimeout(() => api.get("/crews/search", { params: { q } }).then(r => setHits(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(h);
  }, [q]);
  const exact = hits.find(c => c.name.toLowerCase() === q.trim().toLowerCase());
  const create = async () => {
    try { await api.post("/crews", { name: q.trim(), city, country }); toast.success(t("crewCreated")); setQ(""); setCity(""); setCountry(""); load(); }
    catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "object" ? t("crewExists") : d || "Failed"); }
  };
  const join = async id => { try { await api.post(`/crews/${id}/join`); toast.success(t("crewJoinRequested")); setQ(""); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const act = async (cid, uid, action) => { try { await api.post(`/crews/${cid}/members/${uid}/action`, { action }); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  return (
    <div className="mt-8 border-t border-foreground pt-6" data-testid="crew-settings">
      <div className="eyebrow text-primary mb-1 inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{t("crewSection")}</div>
      <p className="text-xs text-muted-foreground mb-3">{t("crewHint")}</p>
      {mine.length > 0 && (
        <ul className="divide-y divide-foreground/20 border border-foreground mb-4" data-testid="my-crews">
          {mine.map(c => (
            <li key={c.crew_id} className="p-3 text-sm" data-testid={`my-crew-${c.crew_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/crews/${c.crew_id}`} className="font-display uppercase text-base hover:text-primary">{c.name}</Link>
                {c.city && <span className="text-xs text-muted-foreground">{[c.city, c.country].filter(Boolean).join(", ")}</span>}
                <span className={`eyebrow !text-[10px] px-1.5 py-0.5 border ml-auto ${c.me_status === "pending" ? "border-foreground/40 text-muted-foreground" : "border-foreground"}`} data-testid={`crew-status-${c.crew_id}`}>{c.me_status === "pending" ? t("crewPending") : c.me_role === "admin" ? t("crewAdmin") : t("crewMember")}</span>
              </div>
              {c.pending?.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid={`crew-requests-${c.crew_id}`}>
                  {c.pending.map(p => <li key={p.user_id} className="flex items-center gap-2 text-xs"><span className="flex-1 truncate">{p.name} · {p.email}</span>
                    <button onClick={() => act(c.crew_id, p.user_id, "approve")} className="gg-btn !py-1 !px-2 text-[10px]" data-testid={`crew-approve-${p.user_id}`}><Check className="w-3 h-3" />{t("approve")}</button>
                    <button onClick={() => act(c.crew_id, p.user_id, "reject")} className="gg-btn !py-1 !px-2 text-[10px]" data-testid={`crew-reject-${p.user_id}`}><X className="w-3 h-3" />{t("reject")}</button></li>)}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      <label className="block"><span className="eyebrow block mb-1.5">{t("crewFind")}</span>
        <input className="gg-input" placeholder={t("crewSearchPlaceholder")} value={q} onChange={e => setQ(e.target.value)} data-testid="crew-search" /></label>
      {q.trim().length >= 2 && (
        <div className="mt-2 border border-foreground divide-y divide-foreground/20" data-testid="crew-results">
          {hits.map(c => (
            <div key={c.crew_id} className="p-2.5 flex items-center gap-2 text-sm">
              <span className="font-display uppercase flex-1 truncate">{c.name}{c.city ? <span className="text-xs text-muted-foreground normal-case font-sans"> · {c.city}</span> : null}</span>
              {c.me_status ? <span className="eyebrow !text-[10px] text-muted-foreground">{c.me_status === "pending" ? t("crewPending") : t("crewMember")}</span>
                : <button onClick={() => join(c.crew_id)} className="gg-btn !py-1.5 !px-2.5 text-xs" data-testid={`crew-join-${c.crew_id}`}>{t("crewRequestJoin")}</button>}
            </div>
          ))}
          {!exact && (
            <div className="p-2.5 text-sm space-y-2" data-testid="crew-create-block">
              <div className="text-xs text-muted-foreground">{t("crewCreateHint", { name: q.trim() })}</div>
              <div className="grid grid-cols-2 gap-2"><input className="gg-input !py-1.5 text-xs" placeholder={t("city")} value={city} onChange={e => setCity(e.target.value)} data-testid="crew-city" /><input className="gg-input !py-1.5 text-xs" placeholder={t("country")} value={country} onChange={e => setCountry(e.target.value)} data-testid="crew-country" /></div>
              <button onClick={create} className="gg-btn-primary !py-1.5 !px-2.5 text-xs" data-testid="crew-create"><Plus className="w-3 h-3" />{t("crewCreate", { name: q.trim() })}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
